/* Newsletter popup: native <dialog> modal (top layer = always centered, dark
   blurred ::backdrop, immune to ancestor transforms). Browse-then-show timing,
   dismissal memory, AJAX submit so the page never reloads/scrolls. */
class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog.newsletter-popup__dialog');
    this.closeBtn = this.querySelector('[data-popup-close]');
    this.form = this.querySelector('.newsletter-popup__form');
    this.successEl = this.querySelector('.newsletter-popup__success');
    this.storageKey = 'newsletter-popup:' + (this.dataset.sectionId || 'default');
    this.frequencyMs = (parseInt(this.dataset.frequency, 10) || 0) * 86400000;
    this.timer = null;

    if (!this.dialog) return;

    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.dialog.close());

    // Click on the ::backdrop area (target is the dialog element itself) closes.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    // Native close fires for the X, Esc, and backdrop click — remember + unlock.
    this.dialog.addEventListener('close', () => {
      document.body.classList.remove('newsletter-popup-open');
      clearTimeout(this.timer);
      this.remember();
    });

    if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

    if (this.suppressed()) return;
    this.scheduleTrigger();
  }

  suppressed() {
    if (this.frequencyMs === 0) return false;
    try {
      const last = parseInt(localStorage.getItem(this.storageKey), 10);
      return last && Date.now() - last < this.frequencyMs;
    } catch (e) {
      return false;
    }
  }

  scheduleTrigger() {
    if (this.dataset.trigger === 'pageviews') {
      const needed = parseInt(this.dataset.pageViews, 10) || 1;
      let views = 0;
      try {
        views = (parseInt(sessionStorage.getItem('newsletter-popup-views'), 10) || 0) + 1;
        sessionStorage.setItem('newsletter-popup-views', views);
      } catch (e) {
        views = needed;
      }
      if (views >= needed) this.open();
    } else {
      const delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
      this.timer = setTimeout(() => this.open(), delay);
    }
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    document.body.classList.add('newsletter-popup-open');
    // Focus the email field, not the close button, and never scroll the page.
    const field = this.dialog.querySelector('input[type="email"]');
    if (field) field.focus({ preventScroll: true });
  }

  onSubmit(event) {
    if (!this.form.checkValidity()) return; // let the browser show native validation
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    if (button) button.setAttribute('aria-busy', 'true');

    fetch(this.form.action, {
      method: 'POST',
      body: new FormData(this.form),
      headers: { Accept: 'text/html' },
    })
      .then(() => this.showSuccess())
      .catch(() => {
        if (button) button.removeAttribute('aria-busy');
      });
  }

  showSuccess() {
    const fields = this.form.querySelector('.newsletter-form__field-wrapper');
    if (fields) fields.hidden = true;
    if (this.successEl) {
      this.successEl.hidden = false;
      this.successEl.focus({ preventScroll: true });
    }
    this.remember();
  }

  remember() {
    if (this.frequencyMs === 0) return;
    try {
      localStorage.setItem(this.storageKey, Date.now().toString());
    } catch (e) {
      /* storage unavailable — fail silently */
    }
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
